import { useEffect, useCallback, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { User } from "@supabase/supabase-js";
import { Tables } from "@/types/database.types";
import {
  userSessionManager,
  CleanupResult,
  SessionContext,
} from "@/services/userSessionManager";
import { useToast } from "@/hooks/use-toast";

export interface UseUserSessionManagerProps {
  user: User | null;
  organization: Tables<"organizations"> | null;
  isLoading: boolean;
}

export interface SessionDebugInfo {
  currentSession: SessionContext | null;
  localStorageKeys: string[];
  sessionStorageKeys: string[];
  userScopedLocalStorage: string[];
  userScopedSessionStorage: string[];
}

export interface UseUserSessionManagerResult {
  performLogout: () => Promise<CleanupResult>;
  performUserChangeCleanup: () => Promise<CleanupResult>;
  getCurrentSessionInfo: () => SessionContext | null;
  getDebugInfo: () => SessionDebugInfo;
  isUserChange: boolean;
}

/**
 * Hook to manage user session lifecycle
 *
 * Integrates with AuthContext to detect user changes and coordinate cleanup
 */
export function useUserSessionManager({
  user,
  organization,
  isLoading,
}: UseUserSessionManagerProps): UseUserSessionManagerResult {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isUserChange, setIsUserChange] = useState(false);
  const cleanupInProgressRef = useRef(false);

  // Track user changes and update session context
  useEffect(() => {
    if (isLoading) return;

    const userChangeDetected = userSessionManager.setCurrentSession(
      user,
      organization?.id || null,
    );

    // Update state with the current user change status
    setIsUserChange(userChangeDetected);

    // If this is a user change (not just login/logout), perform cleanup
    if (userChangeDetected && user) {
      // Check if cleanup is already in progress to prevent race conditions
      if (cleanupInProgressRef.current) {
        console.log(
          "[useUserSessionManager] User change cleanup already in progress, skipping",
        );
        return;
      }

      console.log(
        "[useUserSessionManager] User change detected, performing cleanup",
      );

      // Set flag to prevent concurrent cleanup
      cleanupInProgressRef.current = true;

      userSessionManager
        .performUserChangeCleanup(queryClient)
        .then((result) => {
          if (result.errors.length > 0) {
            console.warn(
              "[useUserSessionManager] Cleanup completed with errors:",
              result.errors,
            );

            // Show toast for any errors during cleanup
            toast({
              variant: "destructive",
              title: "Session Cleanup Warning",
              description:
                "Some data may persist from the previous session. Please refresh if you see unexpected data.",
              duration: 5000,
            });
          } else {
            console.log(
              "[useUserSessionManager] User change cleanup completed successfully",
            );
          }
        })
        .catch((error) => {
          console.error("[useUserSessionManager] Cleanup failed:", error);

          toast({
            variant: "destructive",
            title: "Session Cleanup Failed",
            description:
              "Please refresh the page to ensure clean session state.",
            duration: 7000,
          });
        })
        .finally(() => {
          // Clear the cleanup flag regardless of success or failure
          cleanupInProgressRef.current = false;
          // Reset the user change flag after cleanup completes
          setIsUserChange(false);
        });
    }
  }, [user, organization, isLoading, queryClient, toast, setIsUserChange]);

  // Perform logout with comprehensive cleanup
  const performLogout = useCallback(async (): Promise<CleanupResult> => {
    console.log("[useUserSessionManager] Performing logout cleanup");

    try {
      const result = await userSessionManager.performLogout(queryClient);

      if (result.errors.length > 0) {
        console.warn(
          "[useUserSessionManager] Logout cleanup completed with errors:",
          result.errors,
        );

        toast({
          variant: "destructive",
          title: "Logout Warning",
          description: "Some data may not have been cleared completely.",
          duration: 3000,
        });
      } else {
        console.log(
          "[useUserSessionManager] Logout cleanup completed successfully",
        );
      }

      return result;
    } catch (error) {
      console.error("[useUserSessionManager] Logout cleanup failed:", error);

      toast({
        variant: "destructive",
        title: "Logout Error",
        description: "There was an error during logout. Some data may persist.",
        duration: 5000,
      });

      throw error;
    } finally {
      // Clear the cleanup flag regardless of success or failure
      cleanupInProgressRef.current = false;
      // Reset the user change flag after cleanup completes
      setIsUserChange(false);
    }
  }, [queryClient, toast]);

  // Perform user change cleanup manually
  const performUserChangeCleanup =
    useCallback(async (): Promise<CleanupResult> => {
      // Check if cleanup is already in progress to prevent race conditions
      if (cleanupInProgressRef.current) {
        console.log(
          "[useUserSessionManager] Manual cleanup skipped - already in progress",
        );
        // Return a mock result indicating cleanup was skipped
        return {
          queryCache: false,
          serviceCaches: false,
          localStorage: false,
          sessionStorage: false,
          analytics: false,
          errors: ["Cleanup already in progress"],
        };
      }

      console.log(
        "[useUserSessionManager] Performing manual user change cleanup",
      );

      // Set flag to prevent concurrent cleanup
      cleanupInProgressRef.current = true;

      try {
        const result =
          await userSessionManager.performUserChangeCleanup(queryClient);

        if (result.errors.length > 0) {
          console.warn(
            "[useUserSessionManager] User change cleanup completed with errors:",
            result.errors,
          );

          toast({
            title: "Session Cleanup Warning",
            description: "Some data may persist from the previous session.",
            duration: 3000,
          });
        } else {
          console.log(
            "[useUserSessionManager] User change cleanup completed successfully",
          );

          toast({
            title: "Session Cleaned",
            description: "All user data has been cleared for the new session.",
            duration: 2000,
          });
        }

        return result;
      } catch (error) {
        console.error(
          "[useUserSessionManager] User change cleanup failed:",
          error,
        );

        toast({
          variant: "destructive",
          title: "Cleanup Error",
          description:
            "Failed to clear previous session data. Please refresh the page.",
          duration: 5000,
        });

        throw error;
      }
    }, [queryClient, toast]);

  // Get current session information
  const getCurrentSessionInfo = useCallback(() => {
    return userSessionManager.getCurrentSession();
  }, []);

  // Get debug information about session state
  const getDebugInfo = useCallback(() => {
    return userSessionManager.getDebugInfo();
  }, []);

  return {
    performLogout,
    performUserChangeCleanup,
    getCurrentSessionInfo,
    getDebugInfo,
    isUserChange,
  };
}
